import { Injectable } from '@angular/core';
import { BaseRouteReuseStrategy, type ActivatedRouteSnapshot, type DetachedRouteHandle } from '@angular/router';

/** Внутренняя форма хэндла роутера: `DetachedRouteHandle` публично непрозрачен, а уничтожать ветку надо нам. */
interface RouterDetachedHandle {
  componentRef?: { destroy(): void };
}

/**
 * Возврат в редактор без чёрного релоада: уходя на другую вкладку, ветка редактора не рушится, а
 * откладывается целиком и возвращается тем же экземпляром — канва, выделение и панели уже на месте.
 *
 * Держим РОВНО ОДНУ ветку `/editor/:projectId/:albumId` и ровно один альбом: открыли другой — прежний
 * экземпляр уничтожается сразу, новый альбом поднимается с нуля, чужое состояние переехать не может.
 * `/preview/...` и все остальные маршруты стратегия не трогает вовсе.
 *
 * Смена альбома ВНУТРИ живого редактора сюда не заходит: `shouldReuseRoute` базового класса оставлен
 * как есть, поэтому это по-прежнему смена параметров на том же компоненте, а не detach/attach.
 */
@Injectable({ providedIn: 'root' })
export class AlbumEditorReuseStrategy extends BaseRouteReuseStrategy {
  private cached: { key: string; handle: DetachedRouteHandle } | null = null;

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.keyOf(route) !== null;
  }

  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keyOf(route);
    if (!key) return;
    // Сразу после удачного attach роутер зовёт store(route, null) — это забор ветки из кэша, а не
    // повод что-то уничтожать: компонент уже висит в аутлете.
    if (!handle) {
      if (this.cached?.key === key) this.cached = null;
      return;
    }

    this.dropCached();
    this.cached = { key, handle };
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.keyOf(route);
    if (!key) return false;
    if (!this.cached) return false;
    // Заходим в ДРУГОЙ альбом: отложенный экземпляр прежнего живёт со своими эффектами и подписками
    // на общий на маршрут SyncStore — оставить его рядом с новым значит получить два редактора.
    if (this.cached.key !== key) {
      this.dropCached();
      return false;
    }

    return true;
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.keyOf(route);
    return key !== null && this.cached?.key === key ? this.cached.handle : null;
  }

  /**
   * Забыть отложенную ветку ЭТОГО альбома: модель на сервере переписана (пересборка мастера), и
   * вернуть прежний экземпляр значит показать старую модель и рискнуть записать её поверх новой.
   * Чужой ключ или пустой кэш — ничего не делает; следующий вход в альбом поднимает редактор с нуля.
   */
  forget(projectId: string, albumId: string): void {
    if (this.cached?.key !== `${projectId}/${albumId}`) return;
    this.dropCached();
  }

  /** `null` = маршрут не наш. Иначе ключ = альбом: другой альбом — другой ключ — кэш не подходит. */
  private keyOf(route: ActivatedRouteSnapshot): string | null {
    if (route.routeConfig?.data?.['reuseAlbumEditor'] !== true) return null;
    // Тот же дочерний маршрут висит и под `/preview/...` — кэшируем только редакторскую ветку.
    if (!route.parent?.routeConfig?.path?.startsWith('editor/')) return null;
    const { projectId, albumId } = route.params;
    return projectId && albumId ? `${projectId}/${albumId}` : null;
  }

  private dropCached(): void {
    const handle = this.cached?.handle as RouterDetachedHandle | undefined;
    this.cached = null;
    // Инжектор маршрута (SyncStore и порты) не трогаем: роутер создаёт его один раз на конфигурацию
    // маршрута и переиспользует при следующем входе — это его жизненный цикл, не наш.
    handle?.componentRef?.destroy();
  }
}
